import type { AgentDecisionMemory } from "@/lib/agent-decision";
import { recordAgentOfferPriorEvent } from "@/lib/agent-offer-priors";
import type {
  AgentLocalMemory,
  AgentMemoryEvent,
  AgentMemoryEventType,
} from "@/lib/agent-memory-types";

export type {
  AgentLocalMemory,
  AgentMemoryEvent,
  AgentMemoryEventType,
} from "@/lib/agent-memory-types";

export const AGENT_MEMORY_STORAGE_KEY = "ishvaram_agent_memory_v1";
const AGENT_SESSION_STORAGE_KEY = "ishvaram_agent_session_id_v1";

const MAX_MEMORY_EVENTS = 80;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringOrUndefined(value: unknown) {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function numberOrUndefined(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function normalizeEvent(value: unknown): AgentMemoryEvent | null {
  if (!isRecord(value)) return null;
  const type = stringOrUndefined(value.type);
  if (
    type !== "decision_made" &&
    type !== "offer_shown" &&
    type !== "offer_clicked" &&
    type !== "offer_dismissed" &&
    type !== "checkout_started" &&
    type !== "checkout_abandoned" &&
    type !== "purchase_completed"
  ) {
    return null;
  }

  return {
    type,
    traceId: stringOrUndefined(value.traceId),
    sessionId: stringOrUndefined(value.sessionId),
    sourcePath: stringOrUndefined(value.sourcePath),
    seoIntent: stringOrUndefined(value.seoIntent),
    customerProblem: stringOrUndefined(value.customerProblem),
    activeSpecialist: stringOrUndefined(value.activeSpecialist),
    offerType: stringOrUndefined(value.offerType),
    planId: stringOrUndefined(value.planId),
    bundleId: stringOrUndefined(value.bundleId),
    productSku: stringOrUndefined(value.productSku),
    productName: stringOrUndefined(value.productName),
    checkoutChannel: stringOrUndefined(value.checkoutChannel),
    commerceSystem: stringOrUndefined(value.commerceSystem),
    priceInr: numberOrUndefined(value.priceInr),
    orderId: stringOrUndefined(value.orderId),
    createdAt: stringOrUndefined(value.createdAt) ?? new Date().toISOString(),
  };
}

export function normalizeAgentLocalMemory(value: unknown): AgentLocalMemory {
  const parsed =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return null;
          }
        })()
      : value;

  if (!isRecord(parsed) || !Array.isArray(parsed.events)) return { events: [] };
  return {
    events: parsed.events
      .map(normalizeEvent)
      .filter((event): event is AgentMemoryEvent => Boolean(event))
      .slice(-MAX_MEMORY_EVENTS),
  };
}

export function loadAgentLocalMemory(): AgentLocalMemory {
  if (typeof window === "undefined") return { events: [] };
  try {
    return normalizeAgentLocalMemory(window.localStorage.getItem(AGENT_MEMORY_STORAGE_KEY));
  } catch {
    return { events: [] };
  }
}

export function saveAgentLocalMemory(memory: AgentLocalMemory) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      AGENT_MEMORY_STORAGE_KEY,
      JSON.stringify({ events: memory.events.slice(-MAX_MEMORY_EVENTS) })
    );
  } catch {
    // Memory improves routing, but unavailable storage must not block checkout.
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Server-side mirror (cross-device recovery)
// ──────────────────────────────────────────────────────────────────────────────

// Only these event types have cross-device recovery value. 'decision_made' is
// excluded: too noisy (fires on every decide call), no cross-device value.
const SERVER_SYNC_TYPES = new Set<AgentMemoryEventType>([
  "offer_shown",
  "offer_clicked",
  "offer_dismissed",
  "checkout_started",
  "checkout_abandoned",
  "purchase_completed",
]);

// Module-level debounce state — coalesces rapid recordAgentMemoryEvent calls
// into a single POST after 2 s of quiet.
let _pendingSyncEvents: AgentMemoryEvent[] = [];
let _syncTimer: ReturnType<typeof setTimeout> | undefined;

function flushToServer() {
  const toSync = _pendingSyncEvents.filter((e) => SERVER_SYNC_TYPES.has(e.type));
  _pendingSyncEvents = [];
  _syncTimer = undefined;
  if (toSync.length === 0 || typeof window === "undefined") return;

  const sessionId = getAgentSessionId();
  if (!sessionId) return;

  syncToServer(toSync, sessionId).catch(() => {
    // Server sync is best-effort; localStorage is primary.
  });
}

/**
 * POST a batch of events to the server-side mirror route.
 * Exported for testing (allows callers to mock fetch and assert the call).
 */
export async function syncToServer(
  events: AgentMemoryEvent[],
  sessionId: string,
  userId?: string
): Promise<void> {
  await fetch("/v1/agent/memory/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId, userId, events }),
  });
}

/**
 * GET recent events from the server-side mirror and normalise them.
 * Returns an empty memory on any error — localStorage path is always primary.
 */
export async function fetchServerMemory(
  sessionId: string,
  userId?: string
): Promise<AgentLocalMemory> {
  try {
    const params = new URLSearchParams({ sessionId });
    if (userId) params.set("userId", userId);
    const res = await fetch(`/v1/agent/memory/?${params.toString()}`);
    if (!res.ok) return { events: [] };
    const data = (await res.json()) as { events?: unknown[] };
    return normalizeAgentLocalMemory({ events: data.events ?? [] });
  } catch {
    return { events: [] };
  }
}

/**
 * Union local + server event sets, deduplicating by
 * (sessionId, type, planId, createdAt) composite key.
 * Does NOT write to localStorage — callers decide whether to persist.
 */
export function mergeAgentMemory(
  local: AgentLocalMemory,
  server: AgentLocalMemory
): AgentLocalMemory {
  const seen = new Set(
    local.events.map((e) => `${e.sessionId ?? ""}|${e.type}|${e.planId ?? ""}|${e.createdAt ?? ""}`)
  );
  const merged = [...local.events];
  for (const e of server.events) {
    const key = `${e.sessionId ?? ""}|${e.type}|${e.planId ?? ""}|${e.createdAt ?? ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(e);
    }
  }
  merged.sort((a, b) =>
    (a.createdAt ?? "") < (b.createdAt ?? "")
      ? -1
      : (a.createdAt ?? "") > (b.createdAt ?? "")
        ? 1
        : 0
  );
  return { events: merged.slice(-MAX_MEMORY_EVENTS) };
}

/**
 * Hydrate agent memory from the server and merge with local storage.
 * Opt-in for callers that need cross-device context (e.g. the decide route's
 * client-side caller before computing AgentDecisionOutput).
 *
 * Returns merged memory; does NOT write back to localStorage.
 */
export async function hydrateAgentMemory(
  sessionId: string,
  userId?: string
): Promise<AgentLocalMemory> {
  const [local, server] = await Promise.all([
    Promise.resolve(loadAgentLocalMemory()),
    fetchServerMemory(sessionId, userId),
  ]);
  return mergeAgentMemory(local, server);
}

export function recordAgentMemoryEvent(event: AgentMemoryEvent) {
  if (typeof window === "undefined") return;
  const stamped = { ...event, createdAt: event.createdAt ?? new Date().toISOString() };
  const memory = loadAgentLocalMemory();
  memory.events.push(stamped);
  saveAgentLocalMemory(memory);
  recordAgentOfferPriorEvent(stamped);

  // Queue for debounced server sync (2 s coalescing window).
  _pendingSyncEvents.push(stamped);
  if (_syncTimer !== undefined) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(flushToServer, 2000);
}

export function getAgentSessionId() {
  if (typeof window === "undefined") return undefined;
  try {
    const existing = window.localStorage.getItem(AGENT_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const sessionId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `agent_session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(AGENT_SESSION_STORAGE_KEY, sessionId);
    return sessionId;
  } catch {
    return undefined;
  }
}

export function buildAgentDecisionMemory(memory = loadAgentLocalMemory()): AgentDecisionMemory {
  const purchasedReports = memory.events
    .filter((event) => event.type === "purchase_completed" && event.offerType === "report")
    .map((event) => ({ planId: event.planId }))
    .filter((event): event is { planId: string } => Boolean(event.planId));

  const purchasedKeys = new Set(purchasedReports.map((event) => event.planId));
  const abandonedReports = memory.events
    .filter(
      (event) =>
        (event.type === "checkout_abandoned" || event.type === "checkout_started") &&
        event.offerType === "report" &&
        event.planId &&
        !purchasedKeys.has(event.planId)
    )
    .flatMap((event) =>
      event.planId
        ? [{ planId: event.planId, sourcePath: event.sourcePath, priceInr: event.priceInr }]
        : []
    );

  const dismissedReports = memory.events
    .filter(
      (event) =>
        event.type === "offer_dismissed" &&
        event.offerType === "report" &&
        event.planId &&
        !purchasedKeys.has(event.planId)
    )
    .flatMap((event) => (event.planId ? [{ planId: event.planId }] : []));

  return { purchasedReports, abandonedReports, dismissedReports };
}
